import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronDown, ExternalLink, Bookmark, BookmarkCheck, Eye, ThumbsUp, MessageSquare, User } from "lucide-react";
import { Link } from "react-router-dom";
import { API, trackedVideoUrl, formatCount, timeAgo } from "@/lib/config";
// Imported from the module directly, not the contexts barrel: the barrel pulls in
// PlayerContext, which renders this file, and that cycle left PlayerProvider in its
// temporal dead zone at load time.
import { useCountry } from "@/components/contexts/CountryContext";
import { recordWatch, isSaved, toggleSaved } from "@/lib/storage";
import { ShareMenu } from "@/components/ShareMenu";
import { RankMovement, VelocityBadge } from "@/components/RankMovement";
import { PlayerSlot } from "@/components/player/PlayerSlot";
import axios from "axios";

/**
 * Plays a video without leaving the site.
 *
 * Previously every click handed the visitor to youtube.com and ended the session.
 * Keeping playback here preserves the session, and the "Watch on YouTube" button
 * still routes through the click tracker so attribution is unchanged.
 */
export const VideoModal = ({ video, onClose, onMinimize }) => {
  const { country } = useCountry();
  const [saved, setSaved] = useState(() => (video ? isSaved(video.id) : false));
  const [related, setRelated] = useState([]);

  useEffect(() => {
    if (!video) return;
    recordWatch(video);
    setSaved(isSaved(video.id));
  }, [video]);

  // Escape to close, and stop the page behind from scrolling.
  useEffect(() => {
    if (!video) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [video, onClose]);

  useEffect(() => {
    if (!video) return;
    let cancelled = false;
    const fetchRelated = async () => {
      try {
        const res = await axios.get(`${API}/search`, {
          params: { q: video.title.slice(0, 60), country: country.code, max_results: 8 },
        });
        if (!cancelled) setRelated((res.data.videos || []).filter((v) => v.id !== video.id).slice(0, 6));
      } catch {
        if (!cancelled) setRelated([]);
      }
    };
    fetchRelated();
    return () => {
      cancelled = true;
    };
  }, [video, country.code]);

  if (!video) return null;

  const isShort = video.platform === "shorts";

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm overflow-y-auto"
        onClick={onClose}
        data-testid="video-modal"
      >
        <div className="min-h-full flex items-start justify-center p-3 sm:p-6">
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-4xl rounded-2xl border border-white/10 bg-zinc-950 overflow-hidden shadow-2xl mt-4 mb-8"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div className="flex items-center gap-2 min-w-0">
                {video.trending_rank > 0 && (
                  <span className="shrink-0 text-xs font-bold text-orange-400">#{video.trending_rank}</span>
                )}
                <RankMovement video={video} />
                <h2 className="font-heading text-sm sm:text-base font-bold truncate">{video.title}</h2>
              </div>
              <div className="shrink-0 flex items-center -mr-2">
                {/* Leaves the video playing in the corner instead of ending it -
                    the reason to close rather than minimise is now a real choice,
                    so both are offered side by side. */}
                {onMinimize && (
                  <button
                    onClick={onMinimize}
                    className="flex w-11 h-11 items-center justify-center rounded-lg hover:bg-white/10 transition-colors"
                    aria-label="Minimise"
                  >
                    <ChevronDown className="w-5 h-5" />
                  </button>
                )}
                {/* Primary dismiss control, and it was a 28px target - the one
                    button on this screen that most needs to be easy to hit. */}
                <button
                  onClick={onClose}
                  className="flex w-11 h-11 items-center justify-center rounded-lg hover:bg-white/10 transition-colors"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className={`relative bg-black ${isShort ? "max-w-sm mx-auto" : ""}`}>
              {/* The embed itself belongs to GlobalVideoPlayer, which parks over
                  this box. That is what lets "minimise" keep the video running
                  after this modal is gone. */}
              <PlayerSlot
                video={video}
                surface="modal"
                className={isShort ? "aspect-[9/16]" : "aspect-video"}
              />
            </div>

            <div className="p-4 space-y-4">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-white/60">
                <span className="inline-flex items-center gap-1">
                  <Eye className="w-3 h-3" /> {formatCount(video.view_count_raw) || video.view_count}
                </span>
                <span className="inline-flex items-center gap-1">
                  <ThumbsUp className="w-3 h-3" /> {formatCount(video.like_count_raw) || video.like_count}
                </span>
                {video.comment_count_raw > 0 && (
                  <span className="inline-flex items-center gap-1">
                    <MessageSquare className="w-3 h-3" /> {formatCount(video.comment_count_raw)}
                  </span>
                )}
                <span>{timeAgo(video.published_at)}</span>
              </div>

              <VelocityBadge video={video} />

              <div className="flex flex-wrap items-center gap-2">
                <a
                  href={trackedVideoUrl(video, country.code)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-black text-xs font-bold transition-colors"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Watch on YouTube
                </a>

                <button
                  onClick={() => setSaved(toggleSaved(video))}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/15 hover:bg-white/10 text-xs font-medium transition-colors"
                >
                  {saved ? (
                    <>
                      <BookmarkCheck className="w-3.5 h-3.5 text-orange-400" /> Saved
                    </>
                  ) : (
                    <>
                      <Bookmark className="w-3.5 h-3.5" /> Save
                    </>
                  )}
                </button>

                {video.channel_id && (
                  <Link
                    to={`/channel/${video.channel_id}`}
                    onClick={onClose}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/15 hover:bg-white/10 text-xs font-medium transition-colors"
                  >
                    <User className="w-3.5 h-3.5" /> {video.channel_title}
                  </Link>
                )}

                <ShareMenu video={video} />
              </div>

              {related.length > 0 && (
                <div className="pt-2 border-t border-white/10">
                  <h3 className="text-xs font-bold text-white/50 uppercase tracking-wide mb-3">Up next</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {related.map((r) => (
                      <button
                        key={r.id}
                        onClick={() => {
                          recordWatch(r);
                          // Swapping the iframe src in place keeps the visitor here.
                          window.dispatchEvent(new CustomEvent("tz-play-video", { detail: r }));
                        }}
                        className="text-left group"
                      >
                        <div className="aspect-video rounded-lg overflow-hidden bg-white/5">
                          <img
                            src={r.thumbnail_url}
                            alt={r.title}
                            loading="lazy"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          />
                        </div>
                        <p className="mt-1.5 text-[11px] leading-tight line-clamp-2 text-white/80">{r.title}</p>
                        <p className="text-[10px] text-white/40 truncate">{r.channel_title}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
